import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { UI_LANGUAGES, UI_LANGUAGE_BADGE, isRtlLanguage } from '../../constants/uiLanguages'

/**
 * Compact language menu for header (4 locales) — avoids crowding the bar with four toggles.
 * @param {{ popoverPlacement?: 'below' | 'above' }} props — use `above` in fixed footers (mobile drawer) so the list stays on-screen.
 */
const LanguageSwitcher = ({ popoverPlacement = 'below' }) => {
  const { i18n, t } = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  const short = (i18n.resolvedLanguage || i18n.language || 'en')
    .split('-')[0]
    .trim()
    .toLowerCase()
  const current = UI_LANGUAGES.find((l) => l.code === short) || UI_LANGUAGES[0]
  const badgeLabel = UI_LANGUAGE_BADGE[current.code] ?? String(current.code).toUpperCase()

  useEffect(() => {
    const onOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    if (open) {
      document.addEventListener('mousedown', onOutside)
      return () => document.removeEventListener('mousedown', onOutside)
    }
  }, [open])

  const select = (code) => {
    void i18n.changeLanguage(code).then(() => {
      try {
        window.localStorage.setItem('i18nextLng', code)
      } catch {
        /* ignore */
      }
      document.documentElement.lang = code
      document.documentElement.dir = isRtlLanguage(code) ? 'rtl' : 'ltr'
      setOpen(false)
    })
  }

  /** dir=ltr isolates layout so the menu opens under the pill on mobile even when the page is RTL */
  return (
    <div ref={ref} className="relative w-max max-w-full shrink-0" dir="ltr">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex max-w-full items-center gap-0.5 rounded-full border border-gray-200/90 bg-white py-1 ps-2 pe-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-800 shadow-sm transition hover:border-primary-300 hover:bg-primary-50/40 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 sm:gap-1 sm:py-1.5 sm:ps-2.5 sm:pe-2 sm:text-xs"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={t('common.openLanguageMenu')}
      >
        <span className="shrink-0 whitespace-nowrap tabular-nums" dir="ltr" translate="no">
          {badgeLabel}
        </span>
        <ExpandMore
          className={`shrink-0 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
          sx={{ fontSize: 18 }}
          aria-hidden
        />
      </button>
      {open && (
        <ul
          role="listbox"
          dir="ltr"
          className={`absolute start-0 z-[500] min-w-full w-max max-w-[min(calc(100vw-2rem),12rem)] rounded-xl border border-gray-200 bg-white py-1 text-start shadow-lg ring-1 ring-black/5 ${
            popoverPlacement === 'above' ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
        >
          {UI_LANGUAGES.map(({ code, labelKey }) => (
            <li
              key={code}
              role="option"
              aria-selected={code === short}
              onClick={() => select(code)}
              className={`cursor-pointer px-3 py-2 text-start text-sm transition-colors ${
                code === short
                  ? 'bg-primary-50 font-semibold text-primary-800'
                  : 'text-gray-800 hover:bg-gray-50'
              }`}
            >
              <span className="block truncate text-start" dir="ltr" lang={code}>
                {t(labelKey)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default LanguageSwitcher
